import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  DynamicFormOutlined as DynamicFormOutlinedIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { getCustomForms } from '../../../services/ecommerceApi';

interface FormPickerDialogProps {
  open: boolean;
  configId: string;
  onClose: () => void;
  onSelect: (selection: { slug: string; title: string }) => void;
}

interface FormRow {
  id: string;
  title: string;
  slug: string;
  isActive: boolean;
  displayOrder: number;
  fieldsCount: number;
}

const FormPickerDialog: React.FC<FormPickerDialogProps> = ({ open, configId, onClose, onSelect }) => {
  const [forms, setForms] = useState<FormRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open || !configId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setQuery('');
    getCustomForms(configId)
      .then((res) => {
        if (cancelled) return;
        const list: FormRow[] = (res?.data ?? []).map((f: any) => {
          let fieldsCount = 0;
          if (f?.fieldsJson) {
            try {
              const parsed = JSON.parse(f.fieldsJson);
              if (Array.isArray(parsed)) fieldsCount = parsed.length;
            } catch { /* yutuluyor */ }
          }
          return {
            id: String(f?.id ?? ''),
            title: String(f?.title ?? ''),
            slug: String(f?.slug ?? ''),
            isActive: f?.isActive !== false,
            displayOrder: Number(f?.displayOrder ?? 0),
            fieldsCount
          };
        });
        list.sort((a, b) => a.displayOrder - b.displayOrder);
        setForms(list);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || 'Formlar getirilemedi');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, configId]);

  const filtered = useMemo(() => {
    if (!query.trim()) return forms;
    const q = query.trim().toLowerCase();
    return forms.filter((f) => f.title.toLowerCase().includes(q) || f.slug.toLowerCase().includes(q));
  }, [forms, query]);

  const handlePick = (f: FormRow) => {
    onSelect({ slug: f.slug, title: f.title });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <DynamicFormOutlinedIcon color="primary" fontSize="small" />
        <Typography variant="h6" component="span" sx={{ flex: 1 }}>
          Form Seç
        </Typography>
        <Tooltip title="Kapat">
          <IconButton size="small" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Form ara (başlık veya slug)..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ mb: 2 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Pasif formlar da seçilebilir; ön yüzde yayınlanması için formu Formlar ekranından aktif konuma almanız gerekir.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <DynamicFormOutlinedIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {forms.length === 0 ? 'Henüz oluşturulmuş bir form yok' : 'Eşleşen form bulunamadı'}
            </Typography>
            <Typography variant="caption">
              {forms.length === 0 && 'İÇERİK → Formlar sekmesinden yeni form oluşturabilirsiniz.'}
            </Typography>
          </Box>
        ) : (
          <List
            sx={{
              maxHeight: 380,
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 0
            }}
          >
            {filtered.map((f) => (
              <ListItemButton
                key={f.id}
                onClick={() => handlePick(f)}
                disabled={!f.slug}
                sx={{
                  alignItems: 'flex-start',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 0 },
                  py: 1.25
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>{f.title || 'Adsız Form'}</Typography>
                      <Chip size="small" label={`${f.fieldsCount} alan`} variant="outlined" />
                      {!f.isActive && (
                        <Chip
                          size="small"
                          icon={<VisibilityOffIcon sx={{ fontSize: 12 }} />}
                          label="Pasif"
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      slug: {f.slug || '—'}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormPickerDialog;
